// =============================================================
// DamShareModal.jsx — create & manage EXTERNAL share links (per person).
//
// Two modes:
//   • mode="download" — links to ONE asset (the recipient can download it, or,
//     for images, at a chosen size). Opened from DamAssetModal.
//   • mode="upload"   — upload portals into a collection (external people drop
//     files in with no login). Opened from the DAM library toolbar.
//
// Every link is per-recipient and shows its live status + activity log (view /
// download / upload counts), so uploads and downloads are fully tracked.
// Part of the removable DAM module.
// =============================================================
import { useState, useEffect, useCallback } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { Modal, Btn, Input, Textarea, Select, Badge, Icon, Spinner, useToast } from '../../components/ui';
import { formatInstant } from '../../utils/format';

const EXPIRY_OPTIONS = [
  { value: '30', label: 'Expires in 30 days' },
  { value: '7',  label: 'Expires in 7 days' },
  { value: '90', label: 'Expires in 90 days' },
  { value: '',   label: 'Never expires' },
];

const STATUS_COLOR = { active: B.green, expired: B.textMd, revoked: B.red, exhausted: B.amber };

const absUrl = (path) => `${window.location.origin}${path}`;

export function DamShareModal({ mode = 'download', asset = null, collections = [], defaultCollectionId = '', onClose }) {
  const { toast } = useToast();
  const isDownload = mode === 'download';

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsInit, setNeedsInit] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create-form state
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [maxUses, setMaxUses] = useState('');
  const [allowResize, setAllowResize] = useState(true);
  const [collectionId, setCollectionId] = useState(defaultCollectionId || '');
  const [requestedSpec, setRequestedSpec] = useState('');
  const [maxUploadMB, setMaxUploadMB] = useState('');

  const canResizeOffer = isDownload && !!asset?.isImage;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = isDownload ? `assetId=${asset?.assetID}` : 'kind=upload';
      const res = await api.get(`dam/share-links?${qs}`);
      if (res.needsInit) { setNeedsInit(true); setLinks([]); return; }
      setNeedsInit(false);
      setLinks(res.links || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [isDownload, asset, toast]);

  useEffect(() => { load(); }, [load]);

  const copy = async (path) => {
    try { await navigator.clipboard.writeText(absUrl(path)); toast.success('Link copied to clipboard'); }
    catch { toast.error('Could not copy — long-press / right-click the link to copy it.'); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const body = {
        kind: mode,
        recipientName: recipientName.trim() || null,
        recipientEmail: recipientEmail.trim() || null,
        expiresInDays: expiresInDays ? Number(expiresInDays) : null,
        maxUses: maxUses ? Number(maxUses) : null,
      };
      if (isDownload) {
        body.assetId = asset?.assetID;
        body.allowResize = canResizeOffer ? allowResize : false;
      } else {
        body.collectionId = collectionId || null;
        body.requestedSpec = requestedSpec.trim() || null;
        body.maxUploadMB = maxUploadMB ? Number(maxUploadMB) : null;
      }
      const res = await api.post('dam/share-links', body);
      toast.success('Share link created');
      await copy(res.path);
      // Reset the person-specific fields for the next link.
      setRecipientName(''); setRecipientEmail('');
      await load();
    } catch (e) {
      toast.error(e.status === 400 && e.data?.needsInit ? 'An admin must initialize the module first.' : e.message);
    } finally { setCreating(false); }
  };

  const revoke = async (link) => {
    if (!window.confirm('Turn off this link? Anyone holding it will lose access immediately.')) return;
    try { await api.post(`dam/share-links/${link.shareLinkID}/revoke`, {}); toast.success('Link turned off'); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (link) => {
    if (!window.confirm('Delete this link and its activity log?')) return;
    try { await api.del(`dam/share-links/${link.shareLinkID}`); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const title = isDownload ? 'Share this file externally' : 'External upload links';

  return (
    <Modal title={title} onClose={onClose} width={720}>
      <div style={{ padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.xl }}>
        {/* Intro */}
        <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.6 }}>
          {isDownload
            ? <>Create a private link so someone outside Harvus can download <strong style={{ color: B.text }}>{asset?.title || asset?.fileName}</strong> — no login required.{canResizeOffer ? ' They can pick a size (pixels or MB) if you allow it.' : ''}</>
            : <>Create a private link so someone outside Harvus can upload files straight into the library — no login required. Add a note telling them exactly what you need.</>}
        </div>

        {needsInit ? (
          <div style={{ padding: space.lg, background: B.surfaceMuted, borderRadius: radius.lg, color: B.textMd, fontSize: font.sm }}>
            Share links aren't set up yet. An admin can open <strong>Digital Assets</strong> and click <strong>Initialize module</strong> (or run migration&nbsp;89), then reopen this dialog.
          </div>
        ) : (
          <>
            {/* Create form */}
            <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.xxl, padding: space.lg }}>
              <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text, marginBottom: space.md }}>New link</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
                <Input label="Recipient name" value={recipientName} onChange={setRecipientName} placeholder="e.g. Jane at Acme Design" />
                <Input label="Recipient email (optional)" value={recipientEmail} onChange={setRecipientEmail} type="email" placeholder="jane@acme.com" />
              </div>

              {!isDownload && (
                <>
                  <Select label="Upload into collection" value={collectionId} onChange={setCollectionId}
                    options={[{ value: '', label: 'No collection (uncollected)' }, ...collections.map(c => ({ value: c.collectionID, label: c.name }))]} />
                  <Textarea label="What do you need? (shown to the recipient)" value={requestedSpec} onChange={setRequestedSpec}
                    rows={2} placeholder="e.g. Company logo, at least 1200px wide, PNG with transparent background, under 5MB." />
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
                <Select label="Expiry" value={expiresInDays} onChange={setExpiresInDays} options={EXPIRY_OPTIONS} />
                <Input label={isDownload ? 'Max downloads (optional)' : 'Max uploads (optional)'} value={maxUses} onChange={setMaxUses} type="number" placeholder="Unlimited" />
              </div>

              {!isDownload && (
                <Input label="Max file size in MB (optional)" value={maxUploadMB} onChange={setMaxUploadMB} type="number" placeholder="No limit" />
              )}

              {canResizeOffer && (
                <label style={{ display: 'flex', alignItems: 'center', gap: space.sm, cursor: 'pointer', fontSize: font.sm, color: B.text, marginBottom: space.md }}>
                  <input type="checkbox" checked={allowResize} onChange={e => setAllowResize(e.target.checked)} style={{ width: 16, height: 16, accentColor: B.navy }} />
                  Let the recipient choose a size (pixels or target MB)
                </label>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={create} loading={creating}><Icon name="add" size={14} /> Create link</Btn>
              </div>
            </div>

            {/* Existing links */}
            <div>
              <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text, marginBottom: space.sm }}>
                {isDownload ? 'Links for this file' : 'Upload links'} {links.length > 0 && <span style={{ color: B.textLt }}>({links.length})</span>}
              </div>
              {loading ? <Spinner /> : links.length === 0 ? (
                <div style={{ padding: space.lg, color: B.textLt, fontSize: font.sm, textAlign: 'center' }}>No links yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
                  {links.map(l => <LinkRow key={l.shareLinkID} link={l} onCopy={copy} onRevoke={revoke} onRemove={remove} />)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function LinkRow({ link, onCopy, onRevoke, onRemove }) {
  const { toast } = useToast();
  const [showLog, setShowLog] = useState(false);
  const [events, setEvents] = useState(null);
  const isActive = link.status === 'active';

  const toggleLog = async () => {
    const next = !showLog;
    setShowLog(next);
    if (next && events === null) {
      try { const res = await api.get(`dam/share-links/${link.shareLinkID}/events`); setEvents(res.events || []); }
      catch (e) { toast.error(e.message); setEvents([]); }
    }
  };

  const usesLabel = link.maxUses ? `${link.useCount}/${link.maxUses}` : `${link.useCount}`;

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: '10px 12px', background: B.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>
            {link.recipientName || link.recipientEmail || 'Anyone with the link'}
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>
            {link.recipientEmail && link.recipientName ? `${link.recipientEmail} · ` : ''}
            {link.kind === 'download' ? `${usesLabel} download${link.useCount === 1 ? '' : 's'}` : `${usesLabel} upload${link.useCount === 1 ? '' : 's'}`}
            {link.expiresUTC ? ` · expires ${formatInstant(link.expiresUTC, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
          </div>
        </div>
        <Badge color={STATUS_COLOR[link.status] || B.textMd}>{link.status}</Badge>
        <Btn size="sm" variant="ghost" onClick={() => onCopy(link.path)} disabled={!isActive}>
          <Icon name="attach" size={13} /> Copy
        </Btn>
        <button onClick={toggleLog} title="Activity log" style={iconBtn}>
          <Icon name="list" size={15} color={B.textMd} />
        </button>
        {isActive && (
          <button onClick={() => onRevoke(link)} title="Turn off link" style={iconBtn}>
            <Icon name="lock" size={15} color={B.amber} />
          </button>
        )}
        <button onClick={() => onRemove(link)} title="Delete" style={iconBtn}>
          <Icon name="trash" size={15} color={B.red} />
        </button>
      </div>

      {showLog && (
        <div style={{ marginTop: space.sm, paddingTop: space.sm, borderTop: `1px solid ${B.divider}` }}>
          {events === null ? <div style={{ fontSize: font.xs, color: B.textLt }}>Loading activity…</div>
            : events.length === 0 ? <div style={{ fontSize: font.xs, color: B.textLt }}>No activity yet.</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 160, overflowY: 'auto' }}>
                {events.map(ev => (
                  <div key={ev.eventID} style={{ display: 'flex', gap: space.sm, fontSize: font.xs, color: B.textMd }}>
                    <span style={{ width: 66, fontWeight: weight.semibold, color: B.text, textTransform: 'capitalize' }}>{ev.eventType}</span>
                    <span style={{ width: 130, color: B.textLt }}>{formatInstant(ev.createdUTC)}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.detail || ''}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  border: 'none', background: 'transparent', cursor: 'pointer', padding: 6, borderRadius: radius.md,
  display: 'flex', alignItems: 'center',
};
